import {
    useContext,
    useState
} from "react";
import {
    useWindowDimensions,
    ScrollView,
    View
} from "react-native";
import type RoleFormModalProps from "./type";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitToast,
    NCoreUIKitModal,
    NCoreUIKitTheme,
    TextInput,
    CheckBox,
    Button,
    Text
} from "ncore-ui-kit";
import type {
    Permission
} from "../../type";
import AsyncStorage from "@react-native-async-storage/async-storage";
import {
    AuthContext
} from "../../../../contexts/AuthContext";

const AVAILABLE_PERMISSIONS = [
    {
        label: "Kullanıcıları Görüntüle",
        action: "read",
        module: "users"
    },
    {
        label: "Kullanıcıları Yönet",
        action: "write",
        module: "users"
    },
    {
        label: "Rolleri Görüntüle",
        action: "read",
        module: "roles"
    },
    {
        label: "Rolleri Yönet",
        action: "write",
        module: "roles"
    },
    {
        label: "Finans Görüntüle",
        module: "finances",
        action: "read"
    },
    {
        label: "Finans Yönet",
        module: "finances",
        action: "write"
    },
    {
        label: "Üyeleri Görüntüle",
        module: "members",
        action: "read"
    },
    {
        label: "Üyeleri Yönet",
        module: "members",
        action: "write"
    },
    {
        label: "Kursları Görüntüle",
        module: "courses",
        action: "read"
    },
    {
        label: "Kursları Yönet",
        module: "courses",
        action: "write"
    },
    {
        label: "Öğrencileri Görüntüle",
        module: "students",
        action: "read"
    },
    {
        label: "Öğrencileri Yönet",
        module: "students",
        action: "write"
    },
    {
        label: "Hocaları Görüntüle",
        module: "teachers",
        action: "read"
    },
    {
        label: "Hocaları Yönet",
        module: "teachers",
        action: "write"
    },
    {
        label: "Finans Kategorilerini Görüntüle",
        module: "finance-categories",
        action: "read"
    },
    {
        label: "Finans Kategorilerini Yönet",
        module: "finance-categories",
        action: "write"
    },
    {
        label: "Kumbaraları Görüntüle",
        module: "charityBoxes",
        action: "read"
    },
    {
        label: "Kumbaraları Yönet",
        module: "charityBoxes",
        action: "write"
    }
];

const RoleFormModal = ({
    initialData,
    onSuccess,
    modalId
}: RoleFormModalProps) => {
    const isEdit = !!initialData;
    const {
        radiuses,
        spaces,
        colors
    } = NCoreUIKitTheme.useContext();

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const {
        activeOrganizationFeatures,
        activeOrganizationId,
        user
    } = useContext(AuthContext);

    const {
        width
    } = useWindowDimensions();
    const isMobile = width < 768;

    const [
        formData,
        setFormData
    ] = useState({
        permissions: initialData?.permissions || [] as Permission[],
        role_name: initialData?.role_name || ""
    });

    const [
        isSubmitting,
        setIsSubmitting
    ] = useState(false);

    const handleTogglePermission = (perm: typeof AVAILABLE_PERMISSIONS[0]) => {
        setFormData(prev => {
            const exists = prev.permissions.find(p => p.module === perm.module && p.action === perm.action);
            if (exists) {
                return {
                    ...prev,
                    permissions: prev.permissions.filter(p => !(p.module === perm.module && p.action === perm.action))
                };
            }
            return {
                ...prev,
                permissions: [
                    ...prev.permissions,
                    {
                        action: perm.action,
                        module: perm.module
                    }
                ]
            };
        });
    };

    const handleSubmit = async () => {
        if (!formData.role_name) {
            NCoreUIKitToast.open({
                title: localize("roles-alert-name-required")
            });
            return;
        }

        if (isSubmitting) {
            return;
        }

        setIsSubmitting(true);

        try {
            const token = await AsyncStorage.getItem("authToken");
            const url = isEdit ? `${process.env.API_URL}/roles/${initialData.id}` : `${process.env.API_URL}/roles`;
            const method = isEdit ? "PUT" : "POST";

            const response = await fetch(url, {
                headers: {
                    "Content-Type": "application/json",
                    ...(activeOrganizationId && user?.role === "root" ? {
                        "x-organization-id": activeOrganizationId
                    } : {}),
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify(formData),
                method
            });

            const result = await response.json();
            if (result.status === "success") {
                NCoreUIKitModal.close({
                    id: modalId
                });
                onSuccess();
            } else {
                NCoreUIKitToast.open({
                    title: result.message || (result.errors ? result.errors.map((e: { message: string }) => e.message).join(", ") : localize("roles-toast-unknown-error"))
                });
            }
        } catch (error) {
            console.error(error);
            NCoreUIKitToast.open({
                title: localize("roles-toast-unknown-error")
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return <View
        style={[
            stylesheet.modalContent,
            {
                backgroundColor: colors.content.container.default,
                width: isMobile ? "100%" : 500,
                borderRadius: radiuses.md,
                padding: spaces.spacingXl
            }
        ]}
    >
        <Text
            variant="titleLargeSize"
        >
            {localize("roles-modal-create-title")}
        </Text>
        <View
            style={[
                stylesheet.formContainer,
                {
                    marginTop: spaces.spacingMd,
                    gap: spaces.spacingMd
                }
            ]}
        >
            <TextInput
                placeholder={localize("roles-modal-name-placeholder")}
                title={localize("roles-modal-name-title")}
                onChangeText={(val) => setFormData({
                    ...formData,
                    role_name: val
                })}
                defaultValue={formData.role_name}
                spreadBehaviour="stretch"
            />
            <Text
                style={[
                    stylesheet.permissionsTitle,
                    {
                        marginTop: spaces.spacingSm
                    }
                ]}
                variant="titleMediumSize"
            >
                {localize("roles-modal-permissions-title")}
            </Text>
            <ScrollView
                showsVerticalScrollIndicator={false}
                style={stylesheet.permissionsScroll}
            >
                {AVAILABLE_PERMISSIONS.filter(perm => !activeOrganizationFeatures || activeOrganizationFeatures.length === 0 || activeOrganizationFeatures.includes(perm.module)).map((perm, index) => {
                    const isSelected = !!formData.permissions.find(p => p.module === perm.module && p.action === perm.action);
                    return <CheckBox
                        title={localize(`roles-perm-${perm.module}-${perm.action}` as Parameters<typeof localize>[0])}
                        isChecked={isSelected ? "checked" : undefined}
                        onPress={() => handleTogglePermission(perm)}
                        style={[
                            stylesheet.checkboxRow,
                            {
                                marginVertical: spaces.spacingXs
                            }
                        ]}
                        spreadBehaviour="stretch"
                        key={index}
                    />;
                })}
            </ScrollView>
        </View>
        <View
            style={[
                stylesheet.buttonsContainer,
                {
                    flexDirection: isMobile ? "column-reverse" : "row",
                    marginTop: spaces.spacingXl,
                    gap: spaces.spacingMd
                }
            ]}
        >
            <Button
                onPress={() => NCoreUIKitModal.close({
                    id: modalId
                })}
                title={localize("roles-modal-cancel")}
                spreadBehaviour="stretch"
                type="neutral"
            />
            <Button
                title={localize("roles-modal-save")}
                spreadBehaviour="stretch"
                isLoading={isSubmitting}
                onPress={handleSubmit}
            />
        </View>
    </View>;
};

export default RoleFormModal;
